// src/features/account/components/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  Alert,
  Chip,
  Stack,
  LinearProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Line, LineChart
} from 'recharts';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  Terminal as TerminalIcon,
  ContentCopy as CopyIcon,
  Launch as LaunchIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';
import { format, parseISO, subDays, addDays } from 'date-fns';
import { fetchUsageStats } from '../api/accountApi';
import useAccountStore from '../../../store/accountStore';
import useAuth from '../../auth/hooks/useAuth';
import CreditPointsDisplay from '../../../components/common/CreditPointsDisplay';
import { brand } from '../../../config/brand';

// Custom tooltip component for the chart
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
        <Typography variant="body2" fontWeight="medium">{`Date: ${label || 'Unknown'}`}</Typography>
        <Typography variant="body2" color="primary" fontWeight="medium">
          {`Usage: ${payload[0]?.value || 0} calls`}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { usage, isLoading, error } = useAccountStore();
  const [chartData, setChartData] = useState<Array<{ date: string; usage: number }>>([]);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Fetch usage statistics
  useEffect(() => {
    const loadUsageData = async () => {
      try {
        await fetchUsageStats();
        console.log("Usage data loaded:", useAccountStore.getState().usage);
      } catch (err) {
        console.error("Error loading usage data:", err);
      }
    };
    
    loadUsageData();
  }, []);

  // Process chart data when usage changes
  useEffect(() => {
    if (usage?.dailyUsage && Array.isArray(usage.dailyUsage)) {
      try {
        // Transform the data for the chart, ensuring we have valid dates and counts
        const formattedData = usage.dailyUsage.map(item => {
          // Make sure we have valid date and count properties
          let dateStr = typeof item.date === 'string' ? item.date : new Date().toISOString();
          const count = typeof item.count === 'number' ? item.count : 0;
          
          // Format the date
          try {
            return {
              date: format(new Date(dateStr), 'MMM dd'),
              usage: count
            };
          } catch (e) {
            console.warn(`Error formatting date ${dateStr}:`, e);
            return {
              date: 'Invalid date',
              usage: count
            };
          }
        });
        
        // Fill in missing dates with zero values
        const filledData = fillMissingDates(formattedData);
        setChartData(filledData);
      } catch (error) {
        console.error("Error processing chart data:", error);
        setChartData([]);
      }
    } else {
      console.warn("No daily usage data available or invalid format", usage?.dailyUsage);
      setChartData([]);
    }
  }, [usage]);

  // Fill in missing dates in chart data
  const fillMissingDates = (data: Array<{ date: string; usage: number }>) => {
    if (data.length === 0) return data;
    
    try {
      // Sort data by date
      data.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });
      
      // Get start and end dates
      const firstDateStr = data[0].date;
      const lastDateStr = data[data.length - 1].date;
      
      // Parse dates properly
      let firstDate: Date, lastDate: Date;
      try {
        firstDate = new Date(firstDateStr);
        lastDate = new Date(lastDateStr);
      } catch (e) {
        console.warn("Error parsing dates for filling missing dates:", e);
        return data;
      }
      
      // If dates couldn't be parsed correctly, return original data
      if (isNaN(firstDate.getTime()) || isNaN(lastDate.getTime())) {
        return data;
      }
      
      // Create a map of existing dates
      const dateMap = new Map();
      data.forEach(item => {
        dateMap.set(item.date, item.usage);
      });
      
      // Fill in missing dates
      const filledData = [];
      let currentDate = firstDate;
      
      while (currentDate <= lastDate) {
        const formattedDate = format(currentDate, 'MMM dd');
        filledData.push({
          date: formattedDate,
          usage: dateMap.has(formattedDate) ? dateMap.get(formattedDate) : 0
        });
        currentDate = addDays(currentDate, 1);
      }
      
      return filledData;
    } catch (e) {
      console.error("Error in fillMissingDates:", e);
      return data;
    }
  };

  // Handle reload
  const handleReload = async () => {
    setRefreshing(true);
    try {
      await fetchUsageStats();
    } catch (err) {
      console.error("Error reloading usage data:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Generate a safe balance display string 
  const getFormattedBalance = () => {
    // Check if balance exists and is a number
    if (usage && typeof usage.balance === 'number') {
      return usage.balance.toFixed(2);
    }
    return '0.00';
  };

  // Calculate usage percentage
  const getUsagePercentage = () => {
    if (!usage) return 0;
    
    const totalUsage = typeof usage.totalUsage === 'number' ? usage.totalUsage : 0;
    const quota = 100; // Default quota value - adjust as needed
    
    return Math.min(100, (totalUsage / quota) * 100);
  };

  // Get color based on usage percentage
  const getUsageColor = (percentage: number) => {
    if (percentage < 50) return 'success';
    if (percentage < 80) return 'warning';
    return 'error';
  };

  // If loading and no usage data yet
  if (isLoading && !usage) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Dashboard Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.firstName || 'User'}.
          </Typography>
        </Box>
        
        <Button 
          variant="outlined" 
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleReload}
          disabled={isLoading || refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {typeof error === 'string' ? error : 'An error occurred while loading data'}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        {/* Account Balance Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" component="h2" gutterBottom>
                  Credit Points Balance
                </Typography>
                <Tooltip title="Available Credit Points for API usage">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ mb: 1 }}>
                <CreditPointsDisplay
                  dollars={usage?.balance}
                  size="large"
                  showLabel={false}
                  showTooltip={false}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                Available for API usage
              </Typography>
            </CardContent>
            <Divider />
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<PaymentIcon />}
                onClick={() => navigate('/payment')}
              >
                Get More Credits
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* API Usage Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" component="h2" gutterBottom>
                  API Usage
                </Typography>
                <Tooltip title="Total API calls this period">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h3" sx={{ mr: 1 }}>
                  {typeof usage?.totalUsage === 'number' ? usage.totalUsage : 0}
                </Typography>
                <Chip 
                  size="small" 
                  color={getUsageColor(getUsagePercentage())}
                  label={`${Math.round(getUsagePercentage())}%`}
                />
              </Box>
              
              <Box sx={{ width: '100%', mb: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={getUsagePercentage()} 
                  color={getUsageColor(getUsagePercentage())}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                API calls this period
              </Typography>
            </CardContent>
            <Divider />
            <CardActions>
              <Button 
                variant="outlined" 
                fullWidth
                startIcon={<TrendingUpIcon />}
                onClick={() => navigate('/usage')}
              >
                View Details
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Billing Period Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: brand.gradients.hero,
            color: 'white',
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
            }
          }}>
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TerminalIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.9)' }} />
                  <Typography variant="h6" component="h2" fontWeight="bold">
                    {brand.productName}
                  </Typography>
                </Box>
                <Chip
                  label="v1.0.2"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>

              <Typography variant="body1" sx={{ mb: 2, opacity: 0.95 }}>
                Get started with Web UI or Terminal interface
              </Typography>

              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Box
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.3)',
                    borderRadius: 1,
                    p: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }}
                >
                  {brand.installCommand}
                </Box>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    sx={{ color: 'white' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(brand.installCommand);
                      // You could add a snackbar notification here
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>

            <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />

            <CardActions sx={{ p: 2 }}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.25)',
                  }
                }}
                startIcon={<LaunchIcon />}
                onClick={() => window.open(brand.links.npm, '_blank')}
              >
                View on NPM
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Usage Chart */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            Daily API Usage
          </Typography>
          <Chip 
            label={`Period: ${usage ? format(new Date(usage.periodStart || new Date()), 'MMM dd') : 'N/A'} - ${usage ? format(new Date(usage.periodEnd || new Date()), 'MMM dd') : 'N/A'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
        
        <Box sx={{ height: 300, mt: 2 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#e0e0e0' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: '#e0e0e0' }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="usage" 
                  fill="#3f51b5" 
                  radius={[4, 4, 0, 0]}
                  animationDuration={1000}
                  isAnimationActive={true}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="text.secondary">
                No usage data available for this period
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Additional Information */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Quick Actions
        </Typography>
        
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => navigate('/api-keys')}
            >
              Manage API Keys
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth
              component="a"
              href={brand.links.npm}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Documentation
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={() => navigate('/profile')}
            >
              Update Profile
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button 
              variant="outlined" 
              fullWidth
              color="primary"
              onClick={() => navigate('/payment')}
            >
              Payment Options
            </Button>
          </Grid>
        </Grid>
        
        <Typography variant="body2" color="text.secondary">
          Need help? Check our <Button color="primary" size="small" variant="text" component="a" href={brand.links.npm} target="_blank" rel="noopener noreferrer">documentation</Button> or contact support.
        </Typography>
      </Paper>

      {/* Quick Review */}
      <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <RateReviewIcon sx={{ fontSize: 32, color: brand.colors.fire }} />
          <Box>
            <Typography variant="h6" component="h2" fontWeight={600}>
              Enjoying {brand.productName}?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Share your experience and help other developers discover what you can build.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<RateReviewIcon />}
          onClick={() => navigate('/reviews/submit')}
          sx={{
            px: 3,
            fontWeight: 600,
            background: brand.gradients.button,
            color: brand.buttonTextColor,
            '&:hover': {
              background: brand.gradients.buttonHover,
            },
          }}
        >
          Leave a Review
        </Button>
      </Paper>
    </Box>
  );
};

export default Dashboard;